import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { MetricItem, Service } from 'webapp-types';
import type { SearchProps } from 'webapp/utils/filter';
import { isNumber } from 'webapp/utils/number';
import { isVerified } from 'webapp/utils/verification';

import type { Source, SourceType } from 'webapp/models-custom';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { AnyFilter, FilterOption } from 'webapp/models-custom/filter-sort/filter';

interface FilterSortExtraSettings {
	skipConnected?: boolean;
	filterSortSourceOptions?: FilterOption<string>[];
	isItemsMetricForClient?: boolean;
}

export enum SourceCategory {
	ALL = 'all',
	CONNECTED_SOURCES = 'connected',
	INTEGRATIONS = 'integrations',
}

export enum MetricFilterType {
	ALL = 0,
	FAVORITE = 1,
	POPULAR = 2,
	BASIC = 3,
	QUERY = 4,
	SCRIPT = 5,
	VERIFIED = 6,
}

export const MIN_CHARS_FOR_DIMENSIONS_FILTER = 2;

export default class FilterSortMetricSelect extends FilterSort<MetricItem> {
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	// custom sorting defined in form/metric-select
	@tracked override sortProp = '';

	override searchProps: SearchProps = {
		name: 2,
		'source.title': 1,
		dimensionsString: { weight: 1, minChars: MIN_CHARS_FOR_DIMENSIONS_FILTER },
	};

	connectedSources: Source[] = [];
	showConnectedSources = true;
	showCustomMetrics = true;
	showCalculatedMetrics = true;
	customSourceOptions: FilterOption<string>[] | null = null;
	persistentFilters = [1, 2];
	override settingsNamePrefix = 'metricSelectFilterSettings';

	constructor(options: FilterSortSettings<MetricItem>, extraOptions: FilterSortExtraSettings) {
		super(options);

		this.connectedSources = this.mmSourcesService.sortSourcesWithDatasets(
			this.mmSourcesService.sourceModels,
			'sourceTypePopularity',
			'title',
		);

		const { skipConnected, filterSortSourceOptions, isItemsMetricForClient } = extraOptions;

		this.showConnectedSources = !skipConnected;

		if (filterSortSourceOptions) {
			this.customSourceOptions = filterSortSourceOptions;
		}

		const selectedFilters = this.readSavedSelectedFilters();

		// must validate each saved selected filter if it's on the options list
		const filterAndSelectValidValues = (filter: AnyFilter) => {
			const selectedValues: (string | number)[] = selectedFilters[filter.id] || [];
			let validValues = selectedValues.filter((value) => {
				return !!filter.options.find((o) => o.id === value);
			});
			if (validValues.length === 0) {
				validValues = filter.options[0]?.id ? [filter.options[0].id] : [];
			}
			filter.selectOption(validValues);
		};

		const sourceFilter = this.createFilter<string>({
			id: 1,
			label: 'Data source',
			name: 'category',
			selected: SourceCategory.ALL,
			extraClass: 'metric-select-sources',
			searchableProperties: ['text', 'text2'],
			isMultiSelect: true,
			stringValues: true,
			getOptions: () => {
				return this.sourceOptions;
			},
			filterFn: (item, selected) => {
				const selectedArray = selected;

				if (selectedArray.includes(SourceCategory.ALL)) {
					return true;
				}

				return selectedArray.some((selectedValue) => {
					const genericSourceKey = item.dataSourceTypeKey;
					const isSelectedConnectedSource = isNumber(selectedValue);

					switch (selectedValue) {
						case SourceCategory.CONNECTED_SOURCES:
							return !!item.source;
						case SourceCategory.INTEGRATIONS:
							return !item.source;
						default:
							if (selectedValue === 'Excel') {
								return !!genericSourceKey && genericSourceKey.includes('Excel');
							}

							if (isSelectedConnectedSource) {
								return String(item.spaceAccessId) === selectedValue;
							}

							return genericSourceKey === selectedValue;
					}
				});
			},
		});

		filterAndSelectValidValues(sourceFilter as AnyFilter);

		const typeFilter = this.createFilter<number>({
			id: 2,
			label: 'Show',
			name: 'type',
			selected: MetricFilterType.ALL,
			isMultiSelect: true,
			getOptions: () => {
				return this.typeOptions;
			},
			filterFn: (item, selected) => {
				const selectedArray = selected;

				if (selectedArray.includes(MetricFilterType.ALL)) {
					return true;
				}

				return selectedArray.some((selectedValue) => {
					switch (selectedValue) {
						case MetricFilterType.FAVORITE:
							return item.isFavorite;
						case MetricFilterType.POPULAR: {
							if (!item.isTypeBasic) {
								return false;
							}
							const topMetrics =
								item.sourceType?.metricsByPopularity
									.slice(0, 15)
									.map((m) => m.key) || [];
							return topMetrics.includes(item.key);
						}
						case MetricFilterType.BASIC:
							return item.isTypeBasic;
						case MetricFilterType.QUERY:
							return item.isTypeBuilt;
						case MetricFilterType.SCRIPT:
							return (
								item.isTypeMetricCalculation || item.isTypeMetricCalculationGeneric
							);
						case MetricFilterType.VERIFIED:
							return 'verificationInfo' in item && isVerified(item.verificationInfo);
						default:
							return false;
					}
				});
			},
		});

		filterAndSelectValidValues(typeFilter as AnyFilter);

		if (!isItemsMetricForClient) {
			const tagsFilter = this.createFilter<number>({
				id: 3,
				label: 'Tags',
				name: 'tag',
				selected: 0, // all tags
				isMultiSelect: true,
				getOptions: () => {
					return this.tagOptions;
				},
				filterFn: function (item, selected) {
					return selected.some((selectedValue) =>
						item.tagRecords.some((tagRecord) => {
							const id = +tagRecord.id!;
							return id === selectedValue;
						}),
					);
				},
			});

			filterAndSelectValidValues(tagsFilter as AnyFilter);
		}
	}

	@cached
	get sourceOptions(): FilterOption<SourceCategory | string>[] {
		const customSourceOptions = this.customSourceOptions,
			isDisabled = this.accountService.isDemoAccount;
		let list: FilterOption<SourceCategory | string>[] = [];

		if (customSourceOptions) {
			list = [...customSourceOptions];
		} else {
			let connectedSources: FilterOption<SourceCategory | string>[] = [],
				genericSources: FilterOption<SourceCategory | string>[] = [];

			if (this.showConnectedSources) {
				const datesetIconName: IconName = 'Connector',
					datasetAffixIconName: IconName = 'Table';

				connectedSources = this.connectedSources.map((source: Source) => {
					return {
						id: source.id!,
						text: source.title,
						text2: source.sourceType.name,
						img: source.sourceType.avatar,
						isDisabled,
						statusObject: source.statusObject,
						icon: source.isDataset ? datesetIconName : undefined,
						affixIcon: source.isDataset ? datasetAffixIconName : undefined,
						text3: source.infoForFilter,
						allowDuplicateSelection: true,
					};
				});

				connectedSources.unshift({
					id: SourceCategory.CONNECTED_SOURCES,
					text: 'Data sources',
					class: 'seperator',
					isDisabled,
				});
			}

			const genericSourcesOptions =
				this.mmSourceTypesService.connectableSourceTypeModels.filter(
					(st) => !st.isHidden && !st.showInGroupOnly,
				);

			genericSources = genericSourcesOptions.map((sourceType: SourceType) => {
				return {
					id: sourceType.key,
					text: sourceType.name,
					img: sourceType.avatar,
					isDisabled,
				};
			});

			if (this.showConnectedSources) {
				genericSources.unshift({
					id: SourceCategory.INTEGRATIONS,
					text: 'Available integrations',
					class: 'seperator',
					isDisabled,
				});
			}

			list = [...connectedSources, ...genericSources];
		}

		list.unshift({ id: SourceCategory.ALL, text: 'All' });

		return list;
	}

	get typeOptions(): FilterOption<MetricFilterType>[] {
		const isDisabled = this.accountService.isDemoAccount,
			options: FilterOption<MetricFilterType>[] = [
				{ id: MetricFilterType.ALL, text: 'All', isDisabled },
				...(this.mmMetricsService.showFavoriteAction
					? [{ id: MetricFilterType.FAVORITE, text: 'Favorite' }]
					: []),
				{ id: MetricFilterType.VERIFIED, text: 'Verified', isDisabled },
				{ id: MetricFilterType.POPULAR, text: 'Popular', isDisabled },
				{ id: MetricFilterType.BASIC, text: 'Prebuilt', isDisabled },
			];

		if (this.showCustomMetrics) {
			options.push({ id: MetricFilterType.QUERY, text: 'Built', isDisabled });
		}

		if (this.showCalculatedMetrics) {
			options.push({ id: MetricFilterType.SCRIPT, text: 'Calculated', isDisabled });
		}

		return options;
	}

	@cached
	get tagOptions(): FilterOption<number>[] {
		const tagsArray: number[] = [];

		this.allItems.forEach((item) => {
			item.tagRecords.forEach((tagRecord) => {
				tagsArray.push(+tagRecord.id!);
			});
		});

		const assignedTags: FilterOption<number>[] = this.mmTagsService.getTagCountList(tagsArray);
		if (assignedTags.length) {
			assignedTags.unshift({ id: 0, text: 'All' });
		}

		return assignedTags;
	}

	override readSavedSelectedFilters(): (string[] | undefined)[] {
		const selectedFilters: (string[] | undefined)[] = [],
			savedValues =
				this.viewSettingsManagerService.load<Record<string, string | string[]>>(
					this.settingsName,
					true,
				) || {};

		Object.keys(savedValues || {}).forEach((key) => {
			if (key.startsWith('filter')) {
				const id = parseInt(key.substring(6, key.length - 2), 10);
				const savedValue = savedValues[key];

				// ensure savedValue is always an array for backward compatibility
				if (savedValue !== undefined) {
					selectedFilters[id] = Array.isArray(savedValue) ? savedValue : [savedValue];
				}
			}
		});

		return selectedFilters;
	}
}
